import {
	deleteLandingCustomTemplate,
	fetchLandingCustomTemplates,
	replaceLandingCustomTemplate,
	uploadLandingCustomTemplate,
} from '@/api/landings';
import LoadingWrapper from '@/components/loading-wrapper';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import { InputGroup, InputGroupInput } from '@/components/ui/input-group';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import type { ILandingCustomTemplate } from '@/entities/landing.entity';
import { PageScroll } from '@/layouts/page-layout';
import CustomTemplateZipGuide from '@/pages/landings/components/custom-template-zip-guide';
import LandingsPageCard from '@/pages/landings/components/landings-page-card';
import LandingsGate from '@/pages/landings/landings-gate';
import {
	CUSTOM_TEMPLATE_AI_PROMPT,
	CUSTOM_TEMPLATE_ZIP_HINTS,
} from '@/pages/landings/templates/custom-template-ai-prompt';
import { formatTemplatesCount } from '@/pages/landings/templates/format-templates-count';
import { LayoutTemplateIcon, PlusIcon, RefreshCwIcon, Trash2Icon, UploadIcon } from 'lucide-react';
import { useCallback, useEffect, useRef, useState, type ChangeEvent } from 'react';
import { toast } from 'sonner';

export default function LandingsTemplatesPage() {
	return (
		<PageScroll contentClassName='p-2 flex flex-col gap-3'>
			<LandingsGate>
				<TemplatesContent />
			</LandingsGate>
		</PageScroll>
	);
}

function TemplatesContent() {
	const [templates, setTemplates] = useState<ILandingCustomTemplate[]>([]);
	const [loading, setLoading] = useState(true);
	const [uploadOpen, setUploadOpen] = useState(false);
	const [replaceTarget, setReplaceTarget] = useState<ILandingCustomTemplate | null>(null);
	const [deleteTarget, setDeleteTarget] = useState<ILandingCustomTemplate | null>(null);
	const [title, setTitle] = useState('');
	const [saving, setSaving] = useState(false);
	const [deleting, setDeleting] = useState(false);
	const uploadInputRef = useRef<HTMLInputElement>(null);
	const replaceInputRef = useRef<HTMLInputElement>(null);

	const loadTemplates = useCallback(async () => {
		setLoading(true);
		try {
			const items = await fetchLandingCustomTemplates();
			setTemplates(items);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		void loadTemplates();
	}, [loadTemplates]);

	function openUpload() {
		setTitle('');
		setUploadOpen(true);
	}

	async function handleUploadFile(e: ChangeEvent<HTMLInputElement>) {
		const file = e.target.files?.[0];
		e.target.value = '';
		if (!file) {
			return;
		}

		setSaving(true);
		try {
			await uploadLandingCustomTemplate(file, title || undefined);
			toast.success('Шаблон загружен');
			setUploadOpen(false);
			await loadTemplates();
		} finally {
			setSaving(false);
		}
	}

	async function handleReplaceFile(e: ChangeEvent<HTMLInputElement>) {
		const file = e.target.files?.[0];
		e.target.value = '';
		if (!file || !replaceTarget) {
			return;
		}

		setSaving(true);
		try {
			await replaceLandingCustomTemplate(replaceTarget.id, file, title || undefined);
			toast.success('Шаблон обновлён');
			setReplaceTarget(null);
			await loadTemplates();
		} finally {
			setSaving(false);
		}
	}

	async function handleDelete() {
		if (!deleteTarget) {
			return;
		}
		setDeleting(true);
		try {
			await deleteLandingCustomTemplate(deleteTarget.id);
			toast.success('Шаблон удалён');
			setDeleteTarget(null);
			await loadTemplates();
		} finally {
			setDeleting(false);
		}
	}

	return (
		<>
			<LandingsPageCard
				icon={LayoutTemplateIcon}
				title='Мои шаблоны'
				description={formatTemplatesCount(templates.length)}
				color='chart-4'
				action={
					<Button type='button' size='sm' onClick={openUpload}>
						<PlusIcon size={16} className='mr-1' />
						Загрузить ZIP
					</Button>
				}
			>
				<CustomTemplateZipGuide hints={CUSTOM_TEMPLATE_ZIP_HINTS} prompt={CUSTOM_TEMPLATE_AI_PROMPT} />

				{loading ? (
					<div className='flex justify-center py-8'>
						<Spinner />
					</div>
				) : templates.length === 0 ? (
					<p className='text-sm text-muted-foreground py-4 text-center'>
						Загрузите ZIP со статической страницей, затем выберите шаблон при создании сайта.
					</p>
				) : (
					<div className='flex flex-col gap-2'>
						{templates.map(template => (
							<div
								key={template.id}
								className='rounded-xl border border-border bg-card p-3 shadow-sm flex items-start justify-between gap-3'
							>
								<div className='min-w-0'>
									<p className='font-medium truncate'>{template.title}</p>
									<p className='text-xs text-muted-foreground mt-0.5 font-mono truncate'>{template.slug}</p>
									<p className='text-xs text-muted-foreground mt-1'>
										{new Date(template.createdAt).toLocaleString('ru-RU')}
									</p>
								</div>
								<div className='flex shrink-0 gap-1'>
									<Button
										type='button'
										size='icon'
										variant='ghost'
										onClick={() => {
											setTitle(template.title);
											setReplaceTarget(template);
										}}
									>
										<RefreshCwIcon size={16} />
									</Button>
									<Button
										type='button'
										size='icon'
										variant='ghost'
										className='text-destructive'
										onClick={() => setDeleteTarget(template)}
									>
										<Trash2Icon size={16} />
									</Button>
								</div>
							</div>
						))}
					</div>
				)}
			</LandingsPageCard>

			<Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
				<DialogContent className='sm:max-w-[440px]'>
					<DialogHeader>
						<DialogTitle>Загрузить шаблон</DialogTitle>
						<DialogDescription>ZIP со статикой: index.html и папки css, js, img.</DialogDescription>
					</DialogHeader>
					<div className='flex flex-col gap-4'>
						<div className='flex flex-col gap-1.5'>
							<Label htmlFor='template-title'>Название (необязательно)</Label>
							<InputGroup>
								<InputGroupInput
									id='template-title'
									value={title}
									onChange={e => setTitle(e.target.value)}
									placeholder='Мой лендинг'
									disabled={saving}
								/>
							</InputGroup>
						</div>
						<input
							ref={uploadInputRef}
							type='file'
							accept='.zip,application/zip'
							className='hidden'
							disabled={saving}
							onChange={handleUploadFile}
						/>
						<LoadingWrapper show={saving}>
							<Button
								type='button'
								className='w-full'
								disabled={saving}
								onClick={() => uploadInputRef.current?.click()}
							>
								<UploadIcon />
								{saving ? 'Загрузка…' : 'Выбрать ZIP'}
							</Button>
						</LoadingWrapper>
					</div>
				</DialogContent>
			</Dialog>

			<Dialog open={Boolean(replaceTarget)} onOpenChange={open => !open && setReplaceTarget(null)}>
				<DialogContent className='sm:max-w-[440px]'>
					<DialogHeader>
						<DialogTitle>Заменить ZIP</DialogTitle>
						<DialogDescription>
							Сайты с шаблоном <strong>{replaceTarget?.title}</strong> сразу начнут отдавать новую версию.
						</DialogDescription>
					</DialogHeader>
					<div className='flex flex-col gap-4'>
						<div className='flex flex-col gap-1.5'>
							<Label htmlFor='replace-template-title'>Название (необязательно)</Label>
							<InputGroup>
								<InputGroupInput
									id='replace-template-title'
									value={title}
									onChange={e => setTitle(e.target.value)}
									disabled={saving}
								/>
							</InputGroup>
						</div>
						<input
							ref={replaceInputRef}
							type='file'
							accept='.zip,application/zip'
							className='hidden'
							disabled={saving}
							onChange={handleReplaceFile}
						/>
						<LoadingWrapper show={saving}>
							<Button
								type='button'
								className='w-full'
								disabled={saving}
								onClick={() => replaceInputRef.current?.click()}
							>
								<UploadIcon />
								{saving ? 'Загрузка…' : 'Выбрать новый ZIP'}
							</Button>
						</LoadingWrapper>
					</div>
				</DialogContent>
			</Dialog>

			<Dialog open={Boolean(deleteTarget)} onOpenChange={open => !open && setDeleteTarget(null)}>
				<DialogContent className='sm:max-w-[440px]'>
					<DialogHeader>
						<DialogTitle>Удалить шаблон?</DialogTitle>
						<DialogDescription>
							Шаблон <strong>{deleteTarget?.title}</strong> можно удалить только если нет сайтов с ним.
						</DialogDescription>
					</DialogHeader>
					<DialogFooter className='flex-col sm:flex-col'>
						<div className='w-full'>
							<LoadingWrapper show={deleting}>
								<Button
									type='button'
									variant='destructive'
									className='w-full'
									disabled={deleting}
									onClick={() => void handleDelete()}
								>
									Удалить
								</Button>
							</LoadingWrapper>
						</div>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
